import { InfoCard } from '@/components/contracts/InfoCard'
import { MerchantFeeScheduleDialog } from '@/components/contracts/MerchantFeeScheduleDialog'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { contractColumns } from '@/components/tables/contracts/columns'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { OrganizationType } from '@/lib/enums/OrganizationType'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { canCreateContractForOrganization } from '@/lib/services/contracts/contracts.access'
import {
  contractFilters,
  contractQueries,
  useContracts,
} from '@/lib/services/contracts/contracts.query'
import { formatCurrency, formatPercent } from '@/lib/utils'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import {
  Banknote,
  Calendar,
  CreditCard,
  FileText,
  Info,
  PlusCircle,
  ShoppingCart,
} from 'lucide-react'

const filters = contractFilters().applyFilter({ withStatus: true, sort: 'desc' }).get()

export const Route = createFileRoute('/_authenticated/$orgId/pricing/')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'contracts')) throw notFound()

    await Promise.all([
      context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)),
      context.queryClient.prefetchQuery(
        contractQueries.index({ organizationId: params.orgId, filters })
      ),
    ])
  },
})

function ActiveContractCards({ contract }) {
  const usesVariableServiceFee = contract?.platform_fee_details?.calculation_type === 'tiered_fixed'

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
      <InfoCard
        icon={Calendar}
        tone="mint"
        title="Effective Date"
        description="Pricing effective date"
        value={dayjs(contract.effective_date).format('MMM D, YYYY')}
      />

      <InfoCard
        icon={CreditCard}
        tone="brand"
        title="Merchant Fee"
        description="Accounts v2 card payment recovery"
        action={<MerchantFeeScheduleDialog contract={contract} />}
        value={`${formatPercent(contract?.merchant_fee_details?.value)} + ${formatCurrency(
          contract?.transaction_fee_details?.value
        )}`}
        condition={!!contract.merchant_fee_details && !!contract.transaction_fee_details}
      />

      <InfoCard
        icon={Banknote}
        tone="periwinkle"
        title="Web Order Fee"
        description={
          usesVariableServiceFee
            ? 'Customer-paid tiered website fee'
            : 'Applied to Web product sales after discounts'
        }
        value={
          usesVariableServiceFee
            ? 'Service Fee'
            : formatPercent(contract?.platform_fee_details?.value)
        }
        condition={!!contract.platform_fee_details}
      />

      <InfoCard
        icon={ShoppingCart}
        tone="amethyst"
        title="POS Card Fee"
        description="Applied to eligible POS card sales"
        value={formatPercent(contract?.pos_fee_details?.value)}
        condition={!!contract.pos_fee_details}
      />
    </div>
  )
}

function RouteComponent() {
  // fetch all contracts (sorted server-side by effective_date desc, created_at desc)
  const { data: allContracts = [], isPending } = useContracts({ filters })
  const { organization } = useOrganizationContext()
  const ability = useAbility()
  const path = usePathHelper()
  const canCreateContract = canCreateContractForOrganization(ability, organization)

  // categorize contracts using server-provided status field
  const contractCategories = allContracts?.reduce(
    (acc, contract) => {
      switch (contract.status) {
        case 'active':
          acc.active = contract
          break
        case 'upcoming':
          // ordered by next one up
          acc.upcoming.unshift(contract)
          break
        case 'expired':
          // ordered by most recent active
          acc.expired.push(contract)
          break
      }
      return acc
    },
    { active: null, upcoming: [], expired: [] }
  ) || { active: null, upcoming: [], expired: [] }

  const {
    active: activeContract,
    upcoming: upcomingContracts,
    expired: expiredContracts,
  } = contractCategories

  return (
    <div className="space-y-10">
      <AdminPageHeader
        icon={FileText}
        title="Pricing"
        actions={
          canCreateContract ? (
            <CreateActionButton asChild>
              <Link {...path.pricing.create().link}>Create Pricing Version</Link>
            </CreateActionButton>
          ) : null
        }
      />

      {isPending && (
        <div>
          <Typography variant="h4" className="mb-4 flex items-center gap-2">
            Pricing
          </Typography>
          <IndexDataTable
            mode="client"
            data={[]}
            columns={contractColumns}
            pagination={false}
            isLoading
          />
        </div>
      )}

      {!isPending && activeContract && (
        <div>
          <Typography variant="h4" className="mb-4 flex items-center gap-2">
            Active Pricing
          </Typography>
          <ActiveContractCards contract={activeContract} />
          {organization?.type === OrganizationType.Global.Value && (
            <div className="flex items-center gap-2 mt-3">
              <Info className="h-4 w-4 text-info shrink-0" />
              <Typography variant="label" tone="info">
                These pricing settings are automatically applied to new organizations
              </Typography>
            </div>
          )}
        </div>
      )}

      {!isPending && (upcomingContracts.length > 0 || expiredContracts.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {upcomingContracts.length > 0 && (
            <div>
              <Typography variant="h4" className="mb-4 flex items-center gap-2">
                Upcoming Pricing
              </Typography>
              <IndexDataTable
                mode="client"
                data={upcomingContracts}
                columns={contractColumns}
                pagination={false}
              />
            </div>
          )}

          {expiredContracts.length > 0 && (
            <div>
              <Typography variant="h4" className="mb-4 flex items-center gap-2">
                Expired Pricing
              </Typography>
              <IndexDataTable
                mode="client"
                data={expiredContracts}
                columns={contractColumns}
                pagination={false}
              />
            </div>
          )}
        </div>
      )}

      {!isPending && allContracts.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <Typography variant="h4" tone="muted" className="mb-2">
            No pricing found
          </Typography>
          <Typography variant="body" tone="muted" className="mb-4">
            Create the first pricing version to get started.
          </Typography>
          {canCreateContract ? (
            <Button asChild>
              <Link {...path.pricing.create().link}>
                <PlusCircle />
                Create Pricing Version
              </Link>
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}
